import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { Activity, Eye, Users as UsersIcon, MousePointerClick } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

interface RealtimeSnapshot {
  as_of: string;
  window_minutes: number;
  active: {
    visitors: number;
    users: number;
    sessions: number;
    events: number;
  };
  top_pages: Array<{ url: string; title: string; visitors: number }>;
  recent_events: Array<{
    id: number;
    event_type: string;
    event_name: string | null;
    page_url: string | null;
    user_id: number | null;
    visitor_id: string | null;
    user_email: string | null;
    device_type: string | null;
    source: string | null;
    created_at: string | null;
  }>;
}

interface TimeseriesResponse {
  as_of: string;
  window_minutes: number;
  buckets: Array<{
    bucket: string;
    events: number;
    visitors: number;
    sessions: number;
  }>;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s atrás`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}min atrás`;
  return dt.toLocaleString('pt-BR');
}

function shortUrl(url: string | null): string {
  if (!url) return '—';
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    return url;
  }
}

export function RealtimePage() {
  const snapshot = useQuery<RealtimeSnapshot>({
    queryKey: ['realtime.snapshot'],
    queryFn: async () => (await api().get('/realtime/snapshot')).data,
    refetchInterval: 10_000,
  });

  const timeseries = useQuery<TimeseriesResponse>({
    queryKey: ['realtime.timeseries', 60],
    queryFn: async () =>
      (await api().get('/realtime/timeseries?minutes=60')).data,
    refetchInterval: 30_000,
  });

  const errMsg = snapshot.error
    ? extractErrorMessage(snapshot.error, 'erro ao carregar snapshot')
    : null;
  const a = snapshot.data?.active;

  // Compute timeseries totals for sparkline-like display
  const series = timeseries.data?.buckets ?? [];
  const peakEvents = Math.max(1, ...series.map((b) => b.events));

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <Title>Realtime</Title>
          <Text>
            Atividade ao vivo — janela de 5 min, atualiza a cada 10s.
          </Text>
        </div>
        {snapshot.data && (
          <Badge color="emerald" icon={Activity}>
            Live · {new Date(snapshot.data.as_of).toLocaleTimeString('pt-BR')}
          </Badge>
        )}
      </header>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label="Visitantes (5m)"
          value={snapshot.isLoading ? null : a?.visitors?.toLocaleString('pt-BR')}
          loading={snapshot.isLoading}
          error={errMsg}
          hint="Anônimos + autenticados"
        />
        <KPICard
          label="Usuários (5m)"
          value={snapshot.isLoading ? null : a?.users?.toLocaleString('pt-BR')}
          loading={snapshot.isLoading}
          error={errMsg}
          hint="Logados"
        />
        <KPICard
          label="Sessões (5m)"
          value={snapshot.isLoading ? null : a?.sessions?.toLocaleString('pt-BR')}
          loading={snapshot.isLoading}
          error={errMsg}
        />
        <KPICard
          label="Eventos (5m)"
          value={snapshot.isLoading ? null : a?.events?.toLocaleString('pt-BR')}
          loading={snapshot.isLoading}
          error={errMsg}
        />
      </Grid>

      <Card>
        <Title className="text-base">Eventos por minuto (60min)</Title>
        {timeseries.isLoading && <LoadingSkeleton rows={3} />}
        {!timeseries.isLoading && series.length === 0 && (
          <Text className="text-slate-400">Sem atividade na última hora.</Text>
        )}
        {!timeseries.isLoading && series.length > 0 && (
          <div className="mt-3 flex h-24 items-end gap-0.5">
            {series.map((b) => (
              <div
                key={b.bucket}
                title={`${new Date(b.bucket).toLocaleTimeString('pt-BR')}: ${b.events} eventos, ${b.visitors} visitantes`}
                className="flex-1 rounded-sm bg-blue-500/60 transition hover:bg-blue-400"
                style={{
                  height: `${Math.max(2, (b.events / peakEvents) * 100)}%`,
                }}
              />
            ))}
          </div>
        )}
      </Card>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-blue-300" />
            <Title className="text-base">Páginas ativas (5m)</Title>
          </div>
          {snapshot.isLoading && <LoadingSkeleton rows={4} />}
          {!snapshot.isLoading && (
            <div className="mt-3 space-y-1.5">
              {snapshot.data?.top_pages.length === 0 && (
                <Text className="text-slate-400">Ninguém navegando agora.</Text>
              )}
              {snapshot.data?.top_pages.map((p) => (
                <div
                  key={p.url}
                  className="flex items-center justify-between rounded border border-slate-800 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-slate-200">
                      {p.title || shortUrl(p.url)}
                    </div>
                    <div className="truncate text-xs text-slate-500">
                      {shortUrl(p.url)}
                    </div>
                  </div>
                  <Badge color="blue" icon={UsersIcon}>
                    {p.visitors}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2">
            <MousePointerClick className="h-4 w-4 text-emerald-300" />
            <Title className="text-base">Eventos recentes</Title>
          </div>
          {snapshot.isLoading && <LoadingSkeleton rows={6} />}
          {!snapshot.isLoading && (
            <div className="mt-3 max-h-96 space-y-1 overflow-y-auto">
              {snapshot.data?.recent_events.length === 0 && (
                <Text className="text-slate-400">Sem eventos recentes.</Text>
              )}
              {snapshot.data?.recent_events.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-2 rounded border border-slate-800 px-3 py-1.5 text-xs"
                >
                  <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-300">
                    {e.event_type}
                  </span>
                  {e.event_name && (
                    <span className="text-slate-200">{e.event_name}</span>
                  )}
                  <span className="ml-auto text-slate-500">
                    {fmtRelative(e.created_at)}
                  </span>
                  {e.user_email && (
                    <Badge color="indigo" size="xs">
                      {e.user_email}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>
    </div>
  );
}
